import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  size?: "sm" | "md";
}

export function Select({
  value,
  onChange,
  options,
  placeholder,
  className = "",
  size = "sm",
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selected = options.find((o) => o.value === value);
  const label = selected?.label ?? placeholder ?? "";

  const height = size === "md" ? "h-11" : "h-8";
  const px = size === "md" ? "px-3.5" : "px-3";

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${height} ${px} pr-8 w-full flex items-center rounded-lg border bg-white text-sm outline-none transition-colors cursor-pointer ${
          open
            ? "border-steel-600/50 ring-2 ring-steel-600/10"
            : "border-neutral-200 hover:border-neutral-300"
        }`}
      >
        <span className="truncate text-left text-neutral-800">{label}</span>
        <ChevronDown
          className={`absolute right-2.5 w-3.5 h-3.5 text-neutral-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[180px] bg-white border border-neutral-200 rounded-lg shadow-lg p-1 max-h-60 overflow-auto space-y-0.5">
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-sm text-left rounded-md transition-colors ${
                  isSelected
                    ? "bg-steel-600/5 text-steel-600 font-medium"
                    : "text-neutral-700 hover:bg-neutral-50"
                }`}
              >
                <span className="truncate flex-1">{opt.label}</span>
                {isSelected && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
